import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { WorkflowTransitionProgressDialog } from '../../components/WorkflowTransitionProgressDialog';
import { WorkflowInstance } from '../../hooks/useGetWorkflowInstance';
import { getWorkflowTransitionProgress } from '../../hooks/useWorkflowTransitionProgress';
import { WorkflowTransitionProgressContext } from './WorkflowTransitionProgressContext';
import { useWorkflowTransitionSession } from './WorkflowTransitionSessionContext';

export const WorkflowTransitionProgressProvider = ({
  workflowInstance,
  children,
}: PropsWithChildren<{ workflowInstance: WorkflowInstance }>) => {
  const { startedActions, markStarted, clearStarted } =
    useWorkflowTransitionSession();
  const { name } = workflowInstance;
  const startedAction = startedActions[name];
  const hasShownStartedTransition = useRef(false);

  const showTransitionProgress = useCallback(
    (action: string) => markStarted(name, action),
    [markStarted, name],
  );
  const contextValue = useMemo(
    () => ({ showTransitionProgress }),
    [showTransitionProgress],
  );

  const isDialogOpen =
    !!startedAction &&
    startedAction === getWorkflowTransitionProgress(workflowInstance)?.action;

  useEffect(() => {
    if (isDialogOpen) {
      hasShownStartedTransition.current = true;
    } else if (startedAction && hasShownStartedTransition.current) {
      hasShownStartedTransition.current = false;
      clearStarted(name);
    }
  }, [isDialogOpen, startedAction, clearStarted, name]);

  return (
    <WorkflowTransitionProgressContext.Provider value={contextValue}>
      {children}
      {isDialogOpen && (
        <WorkflowTransitionProgressDialog
          workflowInstance={workflowInstance}
          onClose={() => clearStarted(name)}
         data-track-source-anchor="3bc0ea3f55;workflow-transition-progress-provider-workflow"/>
      )}
    </WorkflowTransitionProgressContext.Provider>
  );
};
